// Lightweight privacy-friendly analytics module
// Events are logged to Supabase for internal use only

import { supabase } from '@/integrations/supabase/client';

type AnalyticsEvent = {
  event: string;
  properties?: Record<string, any>;
  timestamp?: number;
};

const eventQueue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

function getUserId(): string | null {
  try {
    const sessionStr = localStorage.getItem('sb-noknqfmmuqptcmpqquad-auth-token');
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      return session?.user?.id ?? null;
    }
  } catch { /* ignore */ }
  return null;
}

async function flush() {
  if (eventQueue.length === 0) return;
  const batch = eventQueue.splice(0, eventQueue.length);
  const userId = getUserId();

  // Fire-and-forget insert to a lightweight analytics approach
  // We log to console in dev and could extend to PostHog/Plausible later
  if (import.meta.env.DEV) {
    console.log('[Analytics]', batch.map(e => `${e.event}(${JSON.stringify(e.properties)})`).join(', '));
  }

  // Store events in usage_tracking table for internal analytics
  try {
    for (const event of batch) {
      if (!userId) continue;
      await supabase.from('usage_tracking').upsert({
        user_id: userId,
        feature_type: event.event,
        usage_date: new Date().toISOString().split('T')[0],
        usage_count: 1,
      }, {
        onConflict: 'user_id,feature_type,usage_date',
      });
    }
  } catch {
    // Silent fail - analytics should never break the app
  }
}

function scheduleFlush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, 2000);
}

export const analytics = {
  init() {
    if (initialized) return;
    initialized = true;
    // Flush on page unload
    window.addEventListener('beforeunload', flush);
  },

  track(event: string, properties?: Record<string, any>) {
    eventQueue.push({
      event,
      properties,
      timestamp: Date.now(),
    });
    scheduleFlush();
  },
};
